// src/pages/shop/ProductDetailPage.jsx
//
// Step 2: choose options.
//   Add to cart → item goes into the shared cart and the cart dropdown opens.
//   Buy now     → single item goes straight to /checkout?mode=buy-now.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ShopNavbar from "../../components/shop/ShopNavbar";
import { useCart, makeCartItemId, saveBuyNowItem } from "../../context/CartContext";
import { productEndpoint, T, F } from "../../config/shop";

// Keeps the navbar + page frame on every state (loading / error / ready).
function Shell({ children }) {
  return (
    <div className="kaakazini-product-detail-page" style={S.page}>
      <ShopNavbar />
      {children}
    </div>
  );
}

const asList = (value, fallback) => {
  const list = Array.isArray(value) ? value : Array.isArray(fallback) ? fallback : [];
  return list
    .map((entry) =>
      entry && typeof entry === "object" ? entry.name || entry.value || entry.label || "" : entry
    )
    .filter(Boolean)
    .map(String);
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, setIsOpen } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorKind, setErrorKind] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");

  const [activeTab, setActiveTab] = useState("description");
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [notice, setNotice] = useState({ text: "", ok: false, cartLink: false });

  const say = (text, ok = false, cartLink = false) => setNotice({ text, ok, cartLink });

  // ── Load product ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErrorKind(null);
      setProduct(null);
      setSelectedImage(0);
      setSelectedSize("");
      setSelectedColor("");
      setQuantity(1);
      setNotice({ text: "", ok: false, cartLink: false });

      try {
        if (!id) {
          setErrorKind("not_found");
          return;
        }

        const response = await fetch(productEndpoint(id), {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        if (response.status === 404) {
          if (!cancelled) setErrorKind("not_found");
          return;
        }
        if (!response.ok) throw new Error(`Product request failed with status ${response.status}`);

        const data = await response.json();
        if (!cancelled) setProduct(data);
      } catch (error) {
        console.error("Failed to load product:", error);
        if (!cancelled) setErrorKind("network");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  // ── Images ────────────────────────────────────────────────────────────────
  const images = useMemo(() => {
    if (!product) return [];
    const result = [];

    const add = (image) => {
      if (!image) return;
      const url =
        typeof image === "object"
          ? image.image_url || image.url || image.image || image.src || image.original_url || ""
          : image;
      if (typeof url !== "string") return;
      const clean = url.trim();
      if (clean && !result.includes(clean)) result.push(clean);
    };

    if (Array.isArray(product.images)) product.images.forEach(add);
    if (Array.isArray(product.images_detail)) product.images_detail.forEach(add);
    add(product.image_url);
    add(product.image);
    add(product.featured_image);
    return result;
  }, [product]);

  useEffect(() => {
    if (selectedImage >= images.length) setSelectedImage(0);
  }, [images.length, selectedImage]);

  // ── Product data ──────────────────────────────────────────────────────────
  const title = product?.title || product?.name || "Product";

  const price = Number(product?.price ?? product?.selling_price ?? product?.current_price ?? 0);

  const originalRaw = product?.original_price ?? product?.compare_at_price ?? product?.old_price ?? null;
  const originalPrice =
    originalRaw !== null && originalRaw !== "" && !Number.isNaN(Number(originalRaw))
      ? Number(originalRaw)
      : null;
  const discountPercentage =
    originalPrice && originalPrice > price && price >= 0
      ? Math.round(((originalPrice - price) / originalPrice) * 100)
      : null;

  const ratingNum = Number(product?.rating ?? product?.average_rating ?? 0);
  const safeRating = Number.isNaN(ratingNum) ? 0 : Math.max(0, Math.min(5, ratingNum));

  const reviewsNum = Number(product?.reviews_count ?? product?.review_count ?? product?.reviews?.length ?? 0);
  const safeReviewsCount = Number.isNaN(reviewsNum) ? 0 : Math.max(0, reviewsNum);

  // Stock
  const rawStock = product?.stock ?? product?.inventory_quantity ?? product?.quantity_available ?? null;
  const hasStock = rawStock !== null && rawStock !== undefined && rawStock !== "" && !Number.isNaN(Number(rawStock));
  const stock = hasStock ? Number(rawStock) : null;

  const isExplicitlyUnavailable =
    product?.is_available === false ||
    product?.available === false ||
    String(product?.availability || "").toLowerCase() === "out of stock";
  const isOutOfStock = isExplicitlyUnavailable || (stock !== null && stock <= 0);

  const availability =
    product?.availability || (isOutOfStock ? "Out of stock" : stock !== null ? "In stock" : "Available");

  const sizes = useMemo(() => (product ? asList(product.sizes, product.available_sizes) : []), [product]);
  const colors = useMemo(() => (product ? asList(product.colors, product.available_colors) : []), [product]);

  const description =
    product?.description || product?.short_description || "No description is available for this product.";

  const category =
    product?.category_name ||
    product?.category?.name ||
    (typeof product?.category === "string" ? product.category : "") ||
    "Kenyan crafts";

  const seller = product?.vendor || product?.seller_name || "";

  // The Django product id + chosen size/colour identify a cart line. The
  // backend resolves the exact Shopify variant from these options at checkout,
  // so no Shopify IDs are stored in the browser.
  const cartItemId = makeCartItemId(product?.id ?? id, selectedSize, selectedColor);

  // ── Cart helpers ──────────────────────────────────────────────────────────
  const validateSelection = () => {
    if (!product) return say("This product is not available.");
    if (isOutOfStock) return say("This product is currently out of stock.");
    if (sizes.length > 0 && !selectedSize) return say("Please select a size.");
    if (colors.length > 0 && !selectedColor) return say("Please select a color.");
    if (!Number.isInteger(quantity) || quantity < 1) return say("Please select at least one item.");
    if (stock !== null && quantity > stock) return say(`Only ${stock} item${stock === 1 ? "" : "s"} available.`);
    if (!Number.isFinite(price) || price < 0) return say("This product does not have a valid price.");
    return true;
  };

  const buildCartItem = () => ({
    id: product.id,
    cartItemId,
    title,
    price,
    qty: quantity,
    image_url: images[0] || null,
    selectedSize: selectedSize || null,
    selectedColor: selectedColor || null,
    category,
    vendor: seller,
    stock,
  });

  const handleAddToCart = () => {
    if (validateSelection() !== true) return;

    const result = addToCart(buildCartItem());
    if (!result.ok) {
      say(result.message);
      return;
    }
    say(result.message, true, true);
    setIsOpen(true); // show the mini-cart so the customer sees it worked
  };

  const handleBuyNow = () => {
    if (validateSelection() !== true) return;

    if (!saveBuyNowItem(buildCartItem())) {
      say("Unable to start checkout. Please try again.");
      return;
    }
    navigate("/checkout?mode=buy-now");
  };

  const increaseQuantity = () => {
    if (stock !== null && quantity >= stock) return;
    setQuantity((q) => q + 1);
    say("");
  };
  const decreaseQuantity = () => {
    setQuantity((q) => Math.max(1, q - 1));
    say("");
  };

  const goBack = () => (location.key !== "default" ? navigate(-1) : navigate("/"));

  const shareProduct = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: `Check out ${title} on Kaakazini.`, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        say("Product link copied.", true);
      } else {
        say("Unable to copy the product link.");
      }
    } catch (error) {
      if (error?.name !== "AbortError") console.error("Unable to share product:", error);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <Shell>
        <style>{`@keyframes kaakazini-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={S.centerMessage}>
          <div style={S.spinner} />
          <p style={S.messageText}>Loading product…</p>
        </div>
      </Shell>
    );
  }

  if (errorKind === "not_found" || (!errorKind && !product)) {
    return (
      <Shell>
        <div style={S.centerMessage}>
          <h1 style={S.errorTitle}>Product not found</h1>
          <p style={S.messageText}>This product doesn't exist or was removed.</p>
          <button type="button" onClick={() => navigate("/")} style={S.primaryButton}>
            Back to shop
          </button>
        </div>
      </Shell>
    );
  }

  if (errorKind === "network") {
    return (
      <Shell>
        <div style={S.centerMessage}>
          <h1 style={S.errorTitle}>Unable to load product</h1>
          <p style={S.messageText}>We couldn't reach the Kaakazini product service. Check your connection and try again.</p>
          <button type="button" onClick={() => setReloadKey((k) => k + 1)} style={S.primaryButton}>
            Try again
          </button>
        </div>
      </Shell>
    );
  }

  // ── Page ──────────────────────────────────────────────────────────────────
  const starsFilled = Math.max(0, Math.min(5, Math.round(safeRating)));

  return (
    <>
      <style>{`
        .kaakazini-product-detail-page { width: 100%; }
        .kaakazini-product-layout { display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr); gap: 48px; align-items: start; }
        .kaakazini-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
        .kaakazini-link:hover { text-decoration: underline; }

        @media (max-width: 900px) {
          .kaakazini-product-layout { grid-template-columns: 1fr; gap: 32px; }
        }
        @media (max-width: 600px) {
          .kaakazini-container { padding: 16px 16px 40px !important; }
          .kaakazini-product-layout { gap: 26px; }
          .kaakazini-actions { grid-template-columns: 1fr; }
          .kaakazini-main-image-wrapper { border-radius: 14px !important; }
          .kaakazini-title { font-size: 24px !important; }
          .kaakazini-price { font-size: 23px !important; }
          .kaakazini-info-item { align-items: flex-start !important; flex-direction: column; gap: 4px !important; }
          .kaakazini-spec-row { flex-direction: column; gap: 5px !important; }
          .kaakazini-tabs { gap: 18px !important; }
        }
        @media (max-width: 420px) {
          .kaakazini-container { padding-left: 13px !important; padding-right: 13px !important; }
          .kaakazini-title { font-size: 22px !important; }
          .kaakazini-main-image { padding: 10px !important; }
          .kaakazini-thumbnail-button { width: 58px !important; min-width: 58px !important; height: 58px !important; }
        }
      `}</style>

      <Shell>
        <main className="kaakazini-container" style={S.container}>
          <button type="button" onClick={goBack} style={S.backButton}>
            ← Back to shop
          </button>

          <div style={S.breadcrumbs}>
            <Link to="/" style={S.breadcrumbLink}>Shop</Link>
            <span>/</span>
            <span>{category}</span>
            <span>/</span>
            <span>{title}</span>
          </div>

          <section className="kaakazini-product-layout">
            {/* Images */}
            <div style={S.imageSection}>
              <div className="kaakazini-main-image-wrapper" style={S.mainImageWrapper}>
                {images.length > 0 ? (
                  <img
                    className="kaakazini-main-image"
                    src={images[selectedImage]}
                    alt={title}
                    style={S.mainImage}
                    onError={(e) => {
                      e.currentTarget.style.opacity = "0.25";
                    }}
                  />
                ) : (
                  <div style={S.noImage}>No image available</div>
                )}
              </div>

              {images.length > 1 && (
                <div style={S.thumbnailRow}>
                  {images.map((image, index) => (
                    <button
                      className="kaakazini-thumbnail-button"
                      key={`${image}-${index}`}
                      type="button"
                      aria-label={`View product image ${index + 1}`}
                      onClick={() => setSelectedImage(index)}
                      style={{ ...S.thumbnailButton, ...(selectedImage === index ? S.thumbnailButtonActive : {}) }}
                    >
                      <img src={image} alt="" style={S.thumbnail} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div style={S.detailsSection}>
              <div style={S.categoryLabel}>{category}</div>

              <h1 className="kaakazini-title" style={S.title}>{title}</h1>

              {seller && <div style={{ ...S.reviewText, marginBottom: 10 }}>by {seller}</div>}

              <div style={S.ratingRow}>
                <span style={S.stars} aria-label={`${safeRating} out of 5 stars`}>
                  {"★".repeat(starsFilled)}
                  {"☆".repeat(5 - starsFilled)}
                </span>
                <span style={S.ratingText}>{safeRating > 0 ? safeRating.toFixed(1) : "No rating"}</span>
                <span style={S.reviewText}>({safeReviewsCount} reviews)</span>
              </div>

              <div style={S.priceRow}>
                <span className="kaakazini-price" style={S.price}>
                  KSh {Number.isFinite(price) ? price.toLocaleString() : "0"}
                </span>
                {originalPrice && originalPrice > price && (
                  <span style={S.originalPrice}>KSh {originalPrice.toLocaleString()}</span>
                )}
                {discountPercentage > 0 && <span style={S.discount}>{discountPercentage}% off</span>}
              </div>

              <div style={S.availability}>
                <span style={{ ...S.availabilityDot, background: isOutOfStock ? T.faint : T.black }} />
                {availability}
              </div>

              <div style={S.divider} />

              {sizes.length > 0 && (
                <div style={S.optionSection}>
                  <div style={S.optionHeader}>
                    <strong>Size</strong>
                    {selectedSize && <span>{selectedSize}</span>}
                  </div>
                  <div style={S.optionsRow}>
                    {sizes.map((size) => (
                      <button
                        key={size}
                        type="button"
                        aria-pressed={selectedSize === size}
                        onClick={() => {
                          setSelectedSize(size);
                          say("");
                        }}
                        style={{ ...S.optionButton, ...(selectedSize === size ? S.optionButtonActive : {}) }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {colors.length > 0 && (
                <div style={S.optionSection}>
                  <div style={S.optionHeader}>
                    <strong>Color</strong>
                    {selectedColor && <span>{selectedColor}</span>}
                  </div>
                  <div style={S.optionsRow}>
                    {colors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        aria-pressed={selectedColor === color}
                        onClick={() => {
                          setSelectedColor(color);
                          say("");
                        }}
                        style={{ ...S.optionButton, ...(selectedColor === color ? S.optionButtonActive : {}) }}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={S.quantitySection}>
                <strong>Quantity</strong>
                <div style={S.quantityControl}>
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    onClick={decreaseQuantity}
                    disabled={quantity <= 1}
                    style={{ ...S.quantityButton, opacity: quantity <= 1 ? 0.35 : 1, cursor: quantity <= 1 ? "not-allowed" : "pointer" }}
                  >
                    −
                  </button>
                  <span style={S.quantityValue}>{quantity}</span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    onClick={increaseQuantity}
                    disabled={stock !== null && quantity >= stock}
                    style={{
                      ...S.quantityButton,
                      opacity: stock !== null && quantity >= stock ? 0.35 : 1,
                      cursor: stock !== null && quantity >= stock ? "not-allowed" : "pointer",
                    }}
                  >
                    +
                  </button>
                </div>
                {stock !== null && <span style={S.stockText}>{stock} available</span>}
              </div>

              {notice.text && (
                <div role="status" aria-live="polite" style={{ ...S.cartMessage, color: notice.ok ? T.black : T.error }}>
                  {notice.text}
                  {notice.cartLink && (
                    <>
                      {" "}
                      <Link to="/cart" className="kaakazini-link" style={S.inlineLink}>View cart</Link>
                    </>
                  )}
                </div>
              )}

              <div className="kaakazini-actions">
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                  style={{ ...S.addButton, opacity: isOutOfStock ? 0.55 : 1, cursor: isOutOfStock ? "not-allowed" : "pointer" }}
                >
                  Add to cart
                </button>
                <button
                  type="button"
                  onClick={handleBuyNow}
                  disabled={isOutOfStock}
                  style={{ ...S.buyButton, opacity: isOutOfStock ? 0.55 : 1, cursor: isOutOfStock ? "not-allowed" : "pointer" }}
                >
                  Buy now
                </button>
              </div>

              <div style={S.secondaryActions}>
                <button type="button" onClick={() => setIsWishlisted((w) => !w)} aria-pressed={isWishlisted} style={S.secondaryButton}>
                  {isWishlisted ? "♥ Wishlisted" : "♡ Add to wishlist"}
                </button>
                <button type="button" onClick={shareProduct} style={S.secondaryButton}>
                  ↗ Share
                </button>
              </div>

              <div style={S.infoBox}>
                <div className="kaakazini-info-item" style={S.infoBoxItem}>
                  <strong>Category</strong>
                  <span>{category}</span>
                </div>
                <div className="kaakazini-info-item" style={S.infoBoxItem}>
                  <strong>Availability</strong>
                  <span>{availability}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Tabs */}
          <section style={S.tabsSection}>
            <div className="kaakazini-tabs" style={S.tabs} role="tablist">
              {[
                ["description", "Description"],
                ["reviews", `Reviews (${safeReviewsCount})`],
                ["specifications", "Specifications"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === key}
                  onClick={() => setActiveTab(key)}
                  style={{ ...S.tab, ...(activeTab === key ? S.activeTab : {}) }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div style={S.tabContent}>
              {activeTab === "description" && <p style={S.description}>{description}</p>}

              {activeTab === "reviews" && (
                <div>
                  <h3 style={S.tabHeading}>Customer reviews</h3>
                  <p style={S.description}>
                    {safeReviewsCount > 0
                      ? `This product has ${safeReviewsCount} customer review${safeReviewsCount === 1 ? "" : "s"}.`
                      : "No reviews yet."}
                  </p>
                </div>
              )}

              {activeTab === "specifications" && (
                <div style={S.specifications}>
                  <div className="kaakazini-spec-row" style={S.specRow}>
                    <span>Category</span>
                    <strong>{category}</strong>
                  </div>
                  <div className="kaakazini-spec-row" style={S.specRow}>
                    <span>Availability</span>
                    <strong>{availability}</strong>
                  </div>
                  {sizes.length > 0 && (
                    <div className="kaakazini-spec-row" style={S.specRow}>
                      <span>Available sizes</span>
                      <strong>{sizes.join(", ")}</strong>
                    </div>
                  )}
                  {colors.length > 0 && (
                    <div className="kaakazini-spec-row" style={S.specRow}>
                      <span>Available colors</span>
                      <strong>{colors.join(", ")}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </main>
      </Shell>
    </>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: "100vh", width: "100%", background: T.surface, color: T.ink, fontFamily: F.body, boxSizing: "border-box" },
  container: { width: "100%", maxWidth: 1200, margin: "0 auto", padding: "18px 24px 60px", boxSizing: "border-box" },

  backButton: { border: "none", background: "transparent", padding: 0, cursor: "pointer", fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 12, fontFamily: F.body },
  breadcrumbs: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 7, color: T.muted, fontSize: 12, marginBottom: 20, fontFamily: F.body },
  breadcrumbLink: { color: T.ink, textDecoration: "none", fontWeight: 700 },

  imageSection: { width: "100%", minWidth: 0 },
  mainImageWrapper: { width: "100%", aspectRatio: "1 / 1", background: T.white, borderRadius: 18, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" },
  mainImage: { width: "100%", height: "100%", objectFit: "contain", padding: 18, display: "block", boxSizing: "border-box" },
  noImage: { color: T.muted, fontSize: 14, fontFamily: F.body },
  thumbnailRow: { display: "flex", gap: 10, marginTop: 12, overflowX: "auto", padding: "4px 4px 6px" },
  thumbnailButton: { width: 68, height: 68, minWidth: 68, padding: 0, border: "none", borderRadius: 12, overflow: "hidden", background: T.white, cursor: "pointer" },
  thumbnailButtonActive: { outline: `2px solid ${T.black}`, outlineOffset: 2 },
  thumbnail: { width: "100%", height: "100%", objectFit: "cover", display: "block", borderRadius: 12 },

  detailsSection: { minWidth: 0 },
  categoryLabel: { fontFamily: F.body, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: T.muted, marginBottom: 8 },
  title: { fontFamily: F.display, fontSize: 28, lineHeight: 1.25, margin: "0 0 14px", fontWeight: 800, color: T.ink },
  ratingRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 18, flexWrap: "wrap" },
  stars: { letterSpacing: 1, fontSize: 16, color: T.black },
  ratingText: { fontWeight: 700, fontFamily: F.body, fontSize: 13 },
  reviewText: { color: T.muted, fontFamily: F.body, fontSize: 13 },

  priceRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 },
  price: { fontFamily: F.body, fontSize: 26, fontWeight: 800, color: T.black },
  originalPrice: { fontFamily: F.body, fontSize: 14, color: T.muted, textDecoration: "line-through" },
  discount: { fontFamily: F.body, fontSize: 12, fontWeight: 800, padding: "3px 9px", borderRadius: 999, background: T.yellow, color: T.black },

  availability: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, fontFamily: F.body, color: T.inkSoft },
  availabilityDot: { width: 7, height: 7, borderRadius: "50%", display: "inline-block", flexShrink: 0 },
  divider: { height: 1, background: T.borderSoft, margin: "20px 0" },

  optionSection: { marginBottom: 20 },
  optionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, fontFamily: F.body, fontSize: 14, color: T.ink, gap: 20 },
  optionsRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  optionButton: { border: `1px solid ${T.border}`, background: T.white, color: T.ink, borderRadius: 999, padding: "9px 16px", cursor: "pointer", fontWeight: 600, fontFamily: F.body, fontSize: 13 },
  optionButtonActive: { borderColor: T.black, background: T.yellow, color: T.black },

  quantitySection: { display: "flex", alignItems: "center", gap: 14, marginBottom: 16, fontFamily: F.body, fontSize: 14, color: T.ink, flexWrap: "wrap" },
  quantityControl: { display: "flex", alignItems: "center", border: `1px solid ${T.border}`, borderRadius: 999, overflow: "hidden", background: T.white },
  quantityButton: { width: 38, height: 36, border: "none", background: T.white, cursor: "pointer", fontSize: 18, fontFamily: F.body },
  quantityValue: { width: 38, textAlign: "center", fontWeight: 700, fontFamily: F.body },
  stockText: { color: T.muted, fontSize: 12 },

  cartMessage: { fontSize: 13, fontWeight: 700, marginBottom: 14, fontFamily: F.body, lineHeight: 1.5 },
  inlineLink: { color: T.black, fontWeight: 800, textDecoration: "underline" },

  addButton: { border: "none", background: T.black, color: T.yellow, borderRadius: 999, minHeight: 48, cursor: "pointer", fontWeight: 700, fontSize: 14, fontFamily: F.body },
  buyButton: { border: "none", background: T.yellow, color: T.black, borderRadius: 999, minHeight: 48, cursor: "pointer", fontWeight: 700, fontSize: 14, fontFamily: F.body },
  secondaryActions: { display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 22 },
  secondaryButton: { border: "none", background: "transparent", padding: "6px 0", cursor: "pointer", color: T.inkSoft, fontWeight: 600, fontFamily: F.body, fontSize: 13 },

  infoBox: { borderTop: `1px solid ${T.borderSoft}`, paddingTop: 16, display: "grid", gap: 10 },
  infoBoxItem: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, fontFamily: F.body, fontSize: 13, color: T.inkSoft },

  tabsSection: { marginTop: 50, borderTop: `1px solid ${T.borderSoft}` },
  tabs: { display: "flex", gap: 26, borderBottom: `1px solid ${T.borderSoft}`, overflowX: "auto" },
  tab: { border: "none", background: "transparent", padding: "16px 0", cursor: "pointer", color: T.muted, fontWeight: 700, whiteSpace: "nowrap", fontFamily: F.body, fontSize: 14, borderBottom: "2px solid transparent" },
  activeTab: { color: T.black, borderBottom: `2px solid ${T.yellow}` },
  tabContent: { padding: "24px 0 50px" },
  tabHeading: { marginTop: 0, fontFamily: F.display, color: T.ink },
  description: { fontSize: 14.5, lineHeight: 1.75, color: T.inkSoft, whiteSpace: "pre-line", fontFamily: F.body, margin: 0 },
  specifications: { maxWidth: 700 },
  specRow: { display: "flex", justifyContent: "space-between", gap: 20, padding: "12px 0", borderBottom: `1px solid ${T.borderSoft}`, fontFamily: F.body, fontSize: 13.5, color: T.inkSoft },

  centerMessage: { minHeight: "70vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 30, boxSizing: "border-box" },
  spinner: { width: 36, height: 36, border: `4px solid ${T.borderSoft}`, borderTop: `4px solid ${T.black}`, borderRadius: "50%", marginBottom: 18, animation: "kaakazini-spin 0.8s linear infinite" },
  messageText: { color: T.inkSoft, fontSize: 14.5, lineHeight: 1.6, fontFamily: F.body, maxWidth: 420 },
  errorTitle: { fontFamily: F.display, fontSize: 22, fontWeight: 800, marginBottom: 10, color: T.ink },
  primaryButton: { marginTop: 16, border: "none", background: T.black, color: T.yellow, padding: "12px 22px", borderRadius: 999, cursor: "pointer", fontWeight: 700, fontFamily: F.body, fontSize: 13 },
};
